import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { IntegrationServiceId } from '../../common/integrations'
import { useZocksAuth } from '../../components/ScopedBlock/hooks'
import {
  BookingStatus,
  CalendarIntegration,
  CalendarProvider,
  EventTypeAccess,
  SchedulingIntegrationState,
  type SchedulingEventType,
  type SchedulingFilters,
} from '../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetBookingsQuery,
  useGetSchedulingEventTypesQuery,
  useGetUserSchedulingEventTypesQuery,
  useGetUserSchedulingSettingsQuery,
} from '../../store/scheduling/scheduling'
import { isRequestsTabVisible, startOfCurrentMinute, type SchedulingTabKey } from './bookingTabs'
import { isCapable } from './flows/form/scheduleHelpers'

export type SchedulingPageStatus =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'connect-calendar' }
  | { kind: 'select-calendar' }
  | { kind: 'reconnect-calendar'; serviceId?: IntegrationServiceId }
  | { kind: 'no-account-event-types'; isAdmin: boolean }
  | { kind: 'no-user-page-event-types' }
  | { kind: 'ready' }

export interface SchedulingStatusInputs {
  isLoading: boolean
  isError: boolean
  hasCalendar: boolean
  hasSelectableCalendar: boolean
  hasSelectedCalendar: boolean
  calendarNeedsReconnect: boolean
  reconnectServiceId?: IntegrationServiceId
  hasActiveAccountEventTypes: boolean
  hasActiveUserPageEventTypes: boolean
  isAdmin: boolean
}

export const resolveSchedulingPageStatus = (inputs: SchedulingStatusInputs): SchedulingPageStatus => {
  if (inputs.isLoading) {
    return { kind: 'loading' }
  }
  if (inputs.isError) {
    return { kind: 'error' }
  }
  if (!inputs.hasCalendar) {
    return { kind: 'connect-calendar' }
  }
  if (!inputs.hasSelectableCalendar) {
    return { kind: 'reconnect-calendar', serviceId: inputs.reconnectServiceId }
  }
  if (!inputs.hasSelectedCalendar) {
    return { kind: 'select-calendar' }
  }
  if (inputs.calendarNeedsReconnect) {
    return { kind: 'reconnect-calendar', serviceId: inputs.reconnectServiceId }
  }
  if (!inputs.hasActiveAccountEventTypes) {
    return { kind: 'no-account-event-types', isAdmin: inputs.isAdmin }
  }
  if (!inputs.hasActiveUserPageEventTypes) {
    return { kind: 'no-user-page-event-types' }
  }
  return { kind: 'ready' }
}

export const hasActiveUserPageEventTypes = (activeEventTypes: SchedulingEventType[], userPageEventTypeIds: string[]): boolean => {
  const onUserPage = new Set(userPageEventTypeIds)
  return activeEventTypes.some((eventType) => onUserPage.has(eventType.id))
}

const CALENDAR_PROVIDER_INTEGRATION_SERVICE_ID_MAP: { [key in CalendarProvider]: IntegrationServiceId } = {
  [CalendarProvider.EXCHANGE]: IntegrationServiceId.EXCHANGE_CALENDAR,
  [CalendarProvider.GOOGLE]: IntegrationServiceId.GOOGLE_CALENDAR,
  [CalendarProvider.OUTLOOK]: IntegrationServiceId.OUTLOOK_CALENDAR,
}

const getIntegrationServiceIdFrom = (calendarIntegration?: CalendarIntegration): IntegrationServiceId | undefined => {
  return calendarIntegration?.provider ? CALENDAR_PROVIDER_INTEGRATION_SERVICE_ID_MAP[calendarIntegration?.provider] : undefined
}

export const useSchedulingPageStatus = (
  filters: SchedulingFilters,
): {
  status: SchedulingPageStatus
  refetch: () => void
  counts: Record<SchedulingTabKey, number>
  eventTypes?: SchedulingEventType[]
  showRequestsTab: boolean
} => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { hasScope } = useZocksAuth()

  // One first-page bookings query per tab status, each respecting the shared filters; the tab
  // counts come from the responses' page.totalElements. The args match a tab's initial
  // (first-page, default-size) query, so that fetch and its count share one request/cache
  // entry; tabs paginated past page one fetch their own entries.
  const bookingsArgs = (status: BookingStatus) => ({
    ...filters,
    accountId: authenticatedUser.accountId,
    status,
    pageRequest: { page: 0, size: DEFAULT_PAGE_SIZE },
  })
  // Wait for the owner filter (seeded with the current user) so we never fire an unfiltered request.
  const skipBookings = { skip: !filters.userIds?.length }
  const requested = useGetBookingsQuery(bookingsArgs(BookingStatus.REQUESTED), skipBookings)
  const suggested = useGetBookingsQuery(bookingsArgs(BookingStatus.OPPORTUNITY), skipBookings)
  const waiting = useGetBookingsQuery(bookingsArgs(BookingStatus.AWAITING_REPLY), skipBookings)
  const scheduled = useGetBookingsQuery({ ...bookingsArgs(BookingStatus.CONFIRMED), dateFrom: startOfCurrentMinute() }, skipBookings)
  const bookingQueries = [requested, suggested, waiting, scheduled]

  const counts: Record<SchedulingTabKey, number> = {
    requests: requested.data?.page.totalElements ?? 0,
    suggested: suggested.data?.page.totalElements ?? 0,
    waiting: waiting.data?.page.totalElements ?? 0,
    scheduled: scheduled.data?.page.totalElements ?? 0,
  }
  const {
    data: eventTypes,
    isLoading: isEventTypesLoading,
    isError: isEventTypesError,
    refetch: refetchEventTypes,
  } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const {
    data: userEventTypeIds,
    isLoading: isUserEventTypesLoading,
    isError: isUserEventTypesError,
    refetch: refetchUserEventTypes,
  } = useGetUserSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId, userId: authenticatedUser.id })
  const {
    data: accountSettings,
    isLoading: isAccountSettingsLoading,
    isError: isAccountSettingsError,
    refetch: refetchAccountSettings,
  } = useGetAccountSchedulingSettingsQuery({ accountId: authenticatedUser.accountId })
  const {
    data: userSettings,
    isLoading: isUserSettingsLoading,
    isError: isUserSettingsError,
    refetch: refetchUserSettings,
  } = useGetUserSchedulingSettingsQuery({ accountId: authenticatedUser.accountId, userId: authenticatedUser.id })

  const hasCalendar =
    userSettings?.integrations?.calendars?.some((calendar) => calendar.connected && calendar.state === SchedulingIntegrationState.LIVE) ?? false

  const hasSelectedCalendar = userSettings?.integrations?.selectedCalendar != null

  const calendarIntegration = userSettings?.integrations?.calendars?.find((calendar) => calendar.provider === userSettings?.integrations?.selectedCalendar)

  const hasSelectableCalendar = userSettings?.integrations?.calendars?.some(isCapable) ?? false

  const reconnectCalendarIntegration =
    calendarIntegration ?? userSettings?.integrations?.calendars?.find((calendar) => calendar.connected && calendar.state === SchedulingIntegrationState.LIVE)

  const activeEventTypes = (eventTypes ?? []).filter((eventType) => eventType.active)

  const status = resolveSchedulingPageStatus({
    isLoading:
      bookingQueries.some((query) => query.isLoading) || isEventTypesLoading || isUserEventTypesLoading || isAccountSettingsLoading || isUserSettingsLoading,
    isError: bookingQueries.some((query) => query.isError) || isEventTypesError || isUserEventTypesError || isAccountSettingsError || isUserSettingsError,
    hasCalendar,
    hasSelectableCalendar,
    hasSelectedCalendar,
    calendarNeedsReconnect: calendarIntegration ? !isCapable(calendarIntegration) : false,
    reconnectServiceId: getIntegrationServiceIdFrom(reconnectCalendarIntegration),
    hasActiveAccountEventTypes: activeEventTypes.some((eventType) => eventType.access === EventTypeAccess.ACCOUNT),
    hasActiveUserPageEventTypes: hasActiveUserPageEventTypes(activeEventTypes, userEventTypeIds ?? []),
    isAdmin: hasScope('**', 'account_config:update', ['account_config:admin']),
  })

  const refetch = () => {
    requested.refetch()
    suggested.refetch()
    waiting.refetch()
    scheduled.refetch()
    refetchEventTypes()
    refetchUserEventTypes()
    refetchAccountSettings()
    refetchUserSettings()
  }

  return { status, refetch, counts, eventTypes, showRequestsTab: isRequestsTabVisible(accountSettings, userSettings, counts.requests) }
}
